import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Scale, Plus, Pencil, Trash2, X, Save, Loader2, ArrowLeft, RefreshCw,
  Search, Settings, Zap, Wifi, Usb, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { leerPeso, agenteSalud, getAgentUrl, setAgentUrl, webSerialDisponible } from "../lib/balanza";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10 disabled:opacity-50";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

const MODULO_LABEL = {
  MOD_SERIAL_STANDARD: "Serial estándar",
  MOD_COMPAL: "Serial Compal",
  MOD_TCP_IP: "TCP/IP",
};
const PARIDAD_LABEL = { N: "Ninguna", E: "Par", O: "Impar", M: "Mark", S: "Space" };
const FLUJO_LABEL = { none: "Ninguno", rtscts: "Hardware (RTS/CTS)", xonxoff: "Software (XON/XOFF)" };

const vacio = () => ({
  codigo: "", ubicacion: "", equipamiento: "", modelo: "", modulo: "MOD_SERIAL_STANDARD",
  activo: true, ip: "", tcp_port: "", prioridad: 1,
  formato_datos: "ASCII", separador: "", decimales: 0, unidad: "kg",
  utilizar_serial_server: true, permite_digitar_peso: true, modo_simulacion: false,
  serial_puerto: "COM1", serial_baud: 9600, serial_data_bits: 8, serial_stop_bits: 1,
  serial_parity: "N", serial_flow_control: "none", observaciones: "",
});

export default function EquiposBasculaPage() {
  const navigate = useNavigate();
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const slug = activeEmpresaPropia?.slug || "";
  const logoQs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";

  const isAdmin = ["admin", "super_admin", "gerente"].includes(user?.role);
  const can = (a) => isAdmin || hasPermission(`equipos_bascula.${a}`);

  const [equipos, setEquipos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [opciones, setOpciones] = useState({ bauds: [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200] });
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [modal, setModal] = useState(null); // null | {} | equipo
  const [form, setForm] = useState(vacio());
  const [tab, setTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const [modalModelos, setModalModelos] = useState(false);
  const [nuevoModelo, setNuevoModelo] = useState("");

  // Prueba de captura
  const [probando, setProbando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [tramaManual, setTramaManual] = useState("");
  const [agenteUrl, setAgenteUrlState] = useState(getAgentUrl());
  const [agenteEstado, setAgenteEstado] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!can("ver")) { setLoading(false); return; }
    try {
      const [rEq, rMod, rOp] = await Promise.all([
        fetch(`${API}/admin/equipos-bascula${logoQs}`, { headers }),
        fetch(`${API}/admin/equipos-bascula-modelos${logoQs}`, { headers }),
        fetch(`${API}/admin/equipos-bascula/opciones`, { headers }),
      ]);
      if (rEq.ok) setEquipos(await rEq.json());
      if (rMod.ok) setModelos(await rMod.json());
      if (rOp.ok) setOpciones(await rOp.json());
    } catch { toast.error("Error al cargar equipos"); }
    setLoading(false);
  }, [token, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return equipos;
    return equipos.filter(e => [e.codigo, e.ubicacion, e.equipamiento, e.modelo].some(v => (v || "").toLowerCase().includes(q)));
  }, [equipos, busqueda]);

  const abrir = (e) => {
    setForm(e ? { ...vacio(), ...e, tcp_port: e.tcp_port ?? "" } : vacio());
    setTab("general");
    setResultado(null);
    setTramaManual("");
    setAgenteEstado(null);
    setModal(e || {});
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const esTcp = form.modulo === "MOD_TCP_IP";

  const guardar = async () => {
    if (!form.codigo.trim()) { toast.error("El código es obligatorio"); return; }
    setSaving(true);
    try {
      const esEdicion = !!modal?.id;
      const url = esEdicion ? `${API}/admin/equipos-bascula/${modal.id}` : `${API}/admin/equipos-bascula`;
      const payload = {
        ...form,
        tcp_port: form.tcp_port === "" ? null : Number(form.tcp_port),
        prioridad: Number(form.prioridad) || 1,
        decimales: Number(form.decimales) || 0,
        serial_baud: Number(form.serial_baud),
        serial_data_bits: Number(form.serial_data_bits),
        serial_stop_bits: Number(form.serial_stop_bits),
        logo_tipo: slug || undefined,
      };
      const res = await fetch(url, {
        method: esEdicion ? "PUT" : "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Error al guardar"); }
      toast.success(esEdicion ? "Equipo actualizado" : "Equipo creado");
      setModal(null);
      fetchAll();
    } catch (e) { toast.error(e.message || "Error al guardar"); }
    setSaving(false);
  };

  const eliminar = async (e) => {
    if (!window.confirm(`¿Eliminar el equipo ${e.codigo}?`)) return;
    try {
      const res = await fetch(`${API}/admin/equipos-bascula/${e.id}${logoQs}`, { method: "DELETE", headers });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Error al eliminar"); }
      toast.success("Equipo eliminado");
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const agregarModelo = async () => {
    if (!nuevoModelo.trim()) return;
    try {
      const res = await fetch(`${API}/admin/equipos-bascula-modelos`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ nombre: nuevoModelo.trim(), logo_tipo: slug || undefined }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Error"); }
      setNuevoModelo("");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const quitarModelo = async (m) => {
    try {
      const res = await fetch(`${API}/admin/equipos-bascula-modelos/${m.id}${logoQs}`, { method: "DELETE", headers });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Error"); }
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Prueba ────────────────────────────────────────────────────────────────
  const probarAgente = async () => {
    setAgenteUrl(agenteUrl);
    setAgenteEstado({ cargando: true });
    setAgenteEstado(await agenteSalud(agenteUrl.trim() || undefined));
  };

  const probarLectura = async () => {
    setProbando(true);
    setResultado(null);
    try {
      const r = await leerPeso(form, { timeoutMs: 4000 });
      setResultado({ ok: true, ...r });
    } catch (e) {
      setResultado({ ok: false, error: e.message || "Error de lectura" });
    }
    setProbando(false);
  };

  const probarTrama = async () => {
    const { parsearTrama } = await import("../lib/balanza");
    const kg = parsearTrama(tramaManual.replace(/\\r/g, "\r").replace(/\\n/g, "\n"), form);
    setResultado(kg == null
      ? { ok: false, error: "No se encontró un número en la trama con esta configuración" }
      : { ok: true, kg, trama: tramaManual, via: "trama manual", simulado: false });
  };

  if (!can("ver")) {
    return <div className="p-8 text-slate-400">Sin permiso para ver Configuración de Equipos.</div>;
  }

  return (
    <div className="min-h-screen bg-arandu-dark text-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button className={BTN_SEC} onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></button>
          <Scale className="w-6 h-6 text-teal-400" />
          <h1 className="text-xl font-heading font-semibold flex-1">Configuración de Equipos / Báscula</h1>
          <button className={BTN_SEC} onClick={() => { setLoading(true); fetchAll(); }}><RefreshCw className="w-4 h-4" /></button>
          {can("gestionar_catalogos") && (
            <button className={BTN_SEC} onClick={() => setModalModelos(true)}><Settings className="w-4 h-4" /> Modelos</button>
          )}
          {can("crear") && (
            <button className={BTN_PRI} onClick={() => abrir(null)}><Plus className="w-4 h-4" /> Nuevo equipo</button>
          )}
        </div>

        <div className="relative max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className={`${INPUT} pl-8`} placeholder="Buscar por código, ubicación o modelo" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : filtrados.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No hay equipos configurados.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-white/5">
                <tr>
                  <th className="text-left px-4 py-2">Código</th>
                  <th className="text-left px-4 py-2">Ubicación</th>
                  <th className="text-left px-4 py-2">Modelo</th>
                  <th className="text-left px-4 py-2">Comunicación</th>
                  <th className="text-center px-4 py-2">Prioridad</th>
                  <th className="text-center px-4 py-2">Estado</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {filtrados.map(e => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-2 font-medium">{e.codigo}</td>
                    <td className="px-4 py-2 text-slate-300">{e.ubicacion || "—"}</td>
                    <td className="px-4 py-2 text-slate-300">{e.modelo || "—"}</td>
                    <td className="px-4 py-2 text-slate-300">
                      {e.modulo === "MOD_TCP_IP" ? `${e.ip}:${e.tcp_port}` : `${e.serial_puerto} · ${e.serial_baud}`}
                      {e.modo_simulacion && <span className="ml-2 text-[10px] text-amber-400 border border-amber-400/30 rounded px-1">SIMULACIÓN</span>}
                    </td>
                    <td className="px-4 py-2 text-center">{e.prioridad}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${e.activo ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-slate-400 bg-slate-400/10 border-slate-400/20"}`}>
                        {e.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        {(can("editar") || can("probar_captura")) && (
                          <button className="p-1.5 text-slate-400 hover:text-white" title="Abrir ficha" onClick={() => abrir(e)}><Pencil className="w-4 h-4" /></button>
                        )}
                        {can("eliminar") && (
                          <button className="p-1.5 text-slate-400 hover:text-red-400" title="Eliminar" onClick={() => eliminar(e)}><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Ficha del equipo */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h2 className="font-heading font-semibold">{modal.id ? `Equipo ${modal.codigo}` : "Nuevo equipo"}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-1 px-5 pt-3">
              {[["general", "General"], ["comunicacion", "Comunicación"], ["prueba", "Prueba"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${tab === k ? "bg-arandu-blue text-white" : "text-slate-400 hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {tab === "general" && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={LABEL}>Código *</label>
                    <input className={INPUT} value={form.codigo} onChange={(e) => set("codigo", e.target.value.toUpperCase())} placeholder="BAL001" />
                  </div>
                  <div>
                    <label className={LABEL}>Prioridad (1 = primera)</label>
                    <input type="number" min={1} className={INPUT} value={form.prioridad} onChange={(e) => set("prioridad", e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL}>Ubicación</label>
                    <input className={INPUT} value={form.ubicacion || ""} onChange={(e) => set("ubicacion", e.target.value)} placeholder="Báscula de camiones" />
                  </div>
                  <div>
                    <label className={LABEL}>Equipamiento</label>
                    <input className={INPUT} value={form.equipamiento || ""} onChange={(e) => set("equipamiento", e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL}>Modelo</label>
                    <select className={INPUT} value={form.modelo || ""} onChange={(e) => set("modelo", e.target.value)}>
                      <option value="">—</option>
                      {modelos.map(m => <option key={m.id} value={m.nombre}>{m.nombre}</option>)}
                      {form.modelo && !modelos.some(m => m.nombre === form.modelo) && <option value={form.modelo}>{form.modelo}</option>}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Módulo de comunicación</label>
                    <select className={INPUT} value={form.modulo} onChange={(e) => set("modulo", e.target.value)}>
                      {Object.entries(MODULO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2 grid sm:grid-cols-3 gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={form.activo} onChange={(e) => set("activo", e.target.checked)} /> Activo
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input type="checkbox" checked={form.permite_digitar_peso} onChange={(e) => set("permite_digitar_peso", e.target.checked)} /> Permite digitar el peso
                    </label>
                    <label className="flex items-center gap-2 text-sm text-amber-300">
                      <input type="checkbox" checked={form.modo_simulacion} onChange={(e) => set("modo_simulacion", e.target.checked)} /> Modo simulación (pesos de prueba)
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <label className={LABEL}>Observaciones</label>
                    <textarea rows={2} className={INPUT} value={form.observaciones || ""} onChange={(e) => set("observaciones", e.target.value)} />
                  </div>
                </div>
              )}

              {tab === "comunicacion" && (
                <div className="space-y-4">
                  <label className="flex items-start gap-2 text-sm text-slate-300">
                    <input type="checkbox" className="mt-1" checked={form.utilizar_serial_server || esTcp}
                      disabled={esTcp} onChange={(e) => set("utilizar_serial_server", e.target.checked)} />
                    <span>
                      Utilizar agente local (Serial Server)
                      <span className="block text-[11px] text-slate-500">
                        Programa que corre en la PC de la báscula y lee el puerto. Necesario para TCP/IP y para Safari/Firefox.
                        {!esTcp && " Si se desmarca se usa el navegador directamente (Web Serial, solo Chrome/Edge)."}
                      </span>
                    </span>
                  </label>

                  {esTcp ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><label className={LABEL}>IP *</label><input className={INPUT} value={form.ip || ""} onChange={(e) => set("ip", e.target.value)} placeholder="192.168.0.50" /></div>
                      <div><label className={LABEL}>Puerto TCP *</label><input type="number" className={INPUT} value={form.tcp_port} onChange={(e) => set("tcp_port", e.target.value)} placeholder="4001" /></div>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-3 gap-4">
                      <div><label className={LABEL}>Puerto</label><input className={INPUT} value={form.serial_puerto || ""} onChange={(e) => set("serial_puerto", e.target.value.toUpperCase())} placeholder="COM1" /></div>
                      <div><label className={LABEL}>Baudios</label>
                        <select className={INPUT} value={form.serial_baud} onChange={(e) => set("serial_baud", e.target.value)}>
                          {opciones.bauds.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div><label className={LABEL}>Bits de datos</label>
                        <select className={INPUT} value={form.serial_data_bits} onChange={(e) => set("serial_data_bits", e.target.value)}>
                          {[5, 6, 7, 8].map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div><label className={LABEL}>Bits de parada</label>
                        <select className={INPUT} value={form.serial_stop_bits} onChange={(e) => set("serial_stop_bits", e.target.value)}>
                          {[1, 1.5, 2].map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div><label className={LABEL}>Paridad</label>
                        <select className={INPUT} value={form.serial_parity} onChange={(e) => set("serial_parity", e.target.value)}>
                          {Object.entries(PARIDAD_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      </div>
                      <div><label className={LABEL}>Control de flujo</label>
                        <select className={INPUT} value={form.serial_flow_control} onChange={(e) => set("serial_flow_control", e.target.value)}>
                          {Object.entries(FLUJO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-4 gap-4 pt-2 border-t border-white/10">
                    <div><label className={LABEL}>Formato de datos</label>
                      <select className={INPUT} value={form.formato_datos} onChange={(e) => set("formato_datos", e.target.value)}>
                        <option value="ASCII">ASCII</option><option value="HEX">HEX</option><option value="RAW">RAW</option>
                      </select>
                    </div>
                    <div><label className={LABEL}>Separador</label><input className={INPUT} value={form.separador || ""} onChange={(e) => set("separador", e.target.value)} placeholder="ej: , o \\t" /></div>
                    <div><label className={LABEL}>Decimales (0–3)</label><input type="number" min={0} max={3} className={INPUT} value={form.decimales} onChange={(e) => set("decimales", e.target.value)} /></div>
                    <div><label className={LABEL}>Unidad de la trama</label>
                      <select className={INPUT} value={form.unidad} onChange={(e) => set("unidad", e.target.value)}>
                        <option value="kg">kg</option><option value="g">g</option><option value="t">t</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Los decimales se aplican solo si la balanza envía el número sin punto (ej: 012340 con 2 decimales = 123,40). El peso se guarda siempre en kg.
                  </p>
                </div>
              )}

              {tab === "prueba" && (
                <div className="space-y-4">
                  {!can("probar_captura") && <p className="text-sm text-amber-400">No tiene permiso para probar la captura.</p>}
                  {can("probar_captura") && (
                    <>
                      <div className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
                        <label className={LABEL}>Dirección del agente local en esta PC</label>
                        <div className="flex gap-2">
                          <input className={INPUT} value={agenteUrl} onChange={(e) => setAgenteUrlState(e.target.value)} />
                          <button className={BTN_SEC} onClick={probarAgente}><Zap className="w-4 h-4" /> Verificar</button>
                        </div>
                        {agenteEstado && !agenteEstado.cargando && (
                          <p className={`text-xs flex items-center gap-1 ${agenteEstado.ok ? "text-emerald-400" : "text-red-400"}`}>
                            {agenteEstado.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                            {agenteEstado.ok ? `Agente conectado${agenteEstado.version ? ` (v${agenteEstado.version})` : ""}` : agenteEstado.error}
                          </p>
                        )}
                        <p className="text-[11px] text-slate-500">
                          Web Serial en este navegador: {webSerialDisponible() ? "disponible" : "no disponible (use el agente local)"}.
                          La prueba usa la configuración que está en pantalla, aunque todavía no esté guardada.
                        </p>
                      </div>

                      <button className={BTN_PRI} onClick={probarLectura} disabled={probando}>
                        {probando ? <Loader2 className="w-4 h-4 animate-spin" /> : (esTcp ? <Wifi className="w-4 h-4" /> : <Usb className="w-4 h-4" />)}
                        Leer peso ahora
                      </button>

                      <div className="border-t border-white/10 pt-3">
                        <label className={LABEL}>Probar el interpretador con una trama copiada (opcional)</label>
                        <div className="flex gap-2">
                          <input className={`${INPUT} font-mono`} value={tramaManual} onChange={(e) => setTramaManual(e.target.value)} placeholder="ST,GS,   12340 kg" />
                          <button className={BTN_SEC} onClick={probarTrama} disabled={!tramaManual}>Interpretar</button>
                        </div>
                      </div>

                      {resultado && (
                        <div className={`rounded-lg border p-3 text-sm ${resultado.ok ? "border-emerald-400/30 bg-emerald-400/5" : "border-red-400/30 bg-red-400/5"}`}>
                          {resultado.ok ? (
                            <>
                              <div className="text-emerald-400 text-lg font-semibold">{resultado.kg.toLocaleString("es-PY")} kg</div>
                              <div className="text-xs text-slate-400">Vía: {resultado.via}{resultado.simulado ? " (valor simulado, no es de la balanza)" : ""}</div>
                              {resultado.trama && !resultado.simulado && (
                                <div className="text-xs text-slate-500 font-mono mt-1 break-all">Trama: {JSON.stringify(resultado.trama)}</div>
                              )}
                            </>
                          ) : (
                            <div className="text-red-400">{resultado.error}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/10">
              <button className={BTN_SEC} onClick={() => setModal(null)}>Cerrar</button>
              {((modal.id && can("editar")) || (!modal.id && can("crear"))) && (
                <button className={BTN_PRI} onClick={guardar} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Catálogo de modelos */}
      {modalModelos && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-arandu-dark border border-white/10 rounded-xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold">Modelos de balanza</h2>
              <button onClick={() => setModalModelos(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2">
              <input className={INPUT} value={nuevoModelo} onChange={(e) => setNuevoModelo(e.target.value)} placeholder="Nuevo modelo" onKeyDown={(e) => e.key === "Enter" && agregarModelo()} />
              <button className={BTN_PRI} onClick={agregarModelo}><Plus className="w-4 h-4" /></button>
            </div>
            <ul className="divide-y divide-white/5 max-h-64 overflow-y-auto">
              {modelos.map(m => (
                <li key={m.id} className="flex items-center justify-between py-2 text-sm text-slate-300">
                  {m.nombre}
                  <button className="text-slate-500 hover:text-red-400" onClick={() => quitarModelo(m)}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
